import React, { useState, ReactNode, useCallback } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  StepIcon,
  StepIconProps,
  Box,
  Button,
  Typography,
  Paper,
  Fade,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

export interface WizardStep {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  inactiveIcon?: ReactNode;
  content: ReactNode;
  optional?: boolean;
  disabled?: boolean;
  completed?: boolean;
  isValid?: boolean;
}

export interface WizardProps {
  steps: WizardStep[];
  initialStep?: number;
  currentStep?: number;
  setCurrentStep?: (step: number) => void;
  onStepChange?: (currentStep: number, direction: 'next' | 'back', stepData?: any) => Promise<void> | void;
  onComplete?: (allStepsData?: any) => Promise<void> | void;
  onCancel?: () => void;
  onStepClick?: (stepIndex: number) => void;
  nextButtonText?: string;
  backButtonText?: string;
  finishButtonText?: string;
  cancelButtonText?: string;
  loadingText?: string;
  showCancel?: boolean;
  orientation?: 'horizontal' | 'vertical';
  alternativeLabel?: boolean;
  allowStepNavigation?: boolean;
  elevation?: number;
  nonLinear?: boolean;
  showProgress?: boolean;
  className?: string;
  style?: React.CSSProperties;
  showContentHeader?: boolean;
  hideBackButtonOnFirstStep?: boolean;
  isLoading?: boolean;
}

interface CustomStepIconProps extends StepIconProps {
  customIcon?: ReactNode;
  inactiveIcon?: ReactNode;
  stepCompleted?: boolean;
  stepActive?: boolean;
}

interface StepIconWrapperProps {
  ownerState: {
    completed?: boolean;
    active?: boolean;
    error?: boolean;
    inactive?: boolean;
  };
}

const StepIconWrapper = styled('div')<StepIconWrapperProps>(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active
    ? '#83CF9C'
    : ownerState.completed
      ? '#83CF9C'
      : '#D5EFDD',
  zIndex: 1,
  color: (ownerState.active || ownerState.completed)
    ? theme.palette.common.white
    : '#7BB992',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '1.2rem',
  fontWeight: 'bold',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `2px solid ${ownerState.active || ownerState.completed ? '#66BB6A' : 'transparent'}`,

  ...(ownerState.active && {
    boxShadow: `0 4px 12px 0 rgba(131, 207, 156, 0.4)`,
    transform: 'scale(1.1)',
  }),

  ...(ownerState.completed && {
    boxShadow: `0 2px 8px 0 rgba(0, 0, 0, 0.1)`,
  }),

  ...(ownerState.error && {
    backgroundColor: theme.palette.error.main,
    boxShadow: `0 2px 8px 0 ${theme.palette.error.main}30`,
  }),

  '&:hover': {
    transform: ownerState.active ? 'scale(1.1)' : 'scale(1.05)',
  }
}));

const WizardContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  borderRadius: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
    : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  minHeight: 350,
  padding: theme.spacing(2, 0),
  transition: 'all 0.4s ease-in-out',
  position: 'relative',
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(2),
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(0, 0, 0, 0.8)'
    : 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(4px)',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const StepContent = styled(Box)({
  width: '100%',
  height: '100%',
});

const CustomStepIcon: React.FC<CustomStepIconProps> = ({
  active,
  completed,
  customIcon,
  inactiveIcon,
  icon,
  stepCompleted,
  stepActive,
  error
}) => {
  const isCompleted = completed || stepCompleted;
  const isActive = active || stepActive;
  const isInactive = !isActive && !isCompleted && !error;

  let displayIcon;

  if (isActive) {
    displayIcon = customIcon;
  } else if (isCompleted) {
    displayIcon = customIcon;
  } else {
    displayIcon = inactiveIcon || customIcon;
  }

  if (displayIcon) {
    return (
      <StepIconWrapper ownerState={{
        completed: isCompleted,
        active: isActive,
        error,
        inactive: isInactive
      }}>
        {displayIcon}
      </StepIconWrapper>
    );
  }

  return (
    <StepIcon
      active={active}
      completed={completed || stepCompleted}
      icon={icon}
      error={error}
    />
  );
};

const ProgressIndicator: React.FC<{
  currentStep: number;
  totalSteps: number;
  showProgress: boolean;
}> = ({ currentStep, totalSteps, showProgress }) => {
  if (!showProgress) return null;

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <ProgressContainer>
      <Typography variant="body2" color="textSecondary">
        Passo {currentStep + 1} de {totalSteps}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {Math.round(progress)}% concluído
      </Typography>
    </ProgressContainer>
  );
};

const useWizardState = (initialStep: number = 0) => {
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [loading, setLoading] = useState<boolean>(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [errorSteps, setErrorSteps] = useState<Set<number>>(new Set());

  const markStepAsCompleted = useCallback((step: number) => {
    setCompletedSteps(prev => {
      const newCompleted = new Set(prev);
      newCompleted.add(step);
      return newCompleted;
    });
    setErrorSteps(prev => {
      const newErrors = new Set(prev);
      newErrors.delete(step);
      return newErrors;
    });
  }, []);

  const markStepAsError = useCallback((step: number) => {
    setErrorSteps(prev => {
      const newErrors = new Set(prev);
      newErrors.add(step);
      return newErrors;
    });
  }, []);

  const resetStepError = useCallback((step: number) => {
    setErrorSteps(prev => {
      const newErrors = new Set(prev);
      newErrors.delete(step);
      return newErrors;
    });
  }, []);

  return {
    activeStep,
    setActiveStep,
    loading,
    setLoading,
    completedSteps,
    errorSteps,
    markStepAsCompleted,
    markStepAsError,
    resetStepError
  };
};

export const Wizard: React.FC<WizardProps> = ({
  steps,
  initialStep = 0,
  currentStep: externalCurrentStep,
  setCurrentStep: externalSetCurrentStep,
  onStepChange,
  onComplete,
  onCancel,
  onStepClick,
  nextButtonText = 'Próximo',
  backButtonText = 'Voltar',
  finishButtonText = 'Finalizar',
  cancelButtonText = 'Cancelar',
  loadingText = 'Processando...',
  showCancel = false,
  orientation = 'horizontal',
  alternativeLabel = false,
  allowStepNavigation = true,
  elevation = 3,
  nonLinear = false,
  showProgress = true,
  className,
  style,
  showContentHeader = true,
  hideBackButtonOnFirstStep = false,
  isLoading: externalLoading,
}) => {

  const {
    activeStep: internalActiveStep,
    setActiveStep: setInternalActiveStep,
    loading: internalLoading,
    setLoading,
    completedSteps,
    errorSteps,
    markStepAsCompleted,
    markStepAsError,
    resetStepError
  } = useWizardState(initialStep);


  const activeStep = externalCurrentStep !== undefined ? externalCurrentStep : internalActiveStep;
  const setActiveStep = externalSetCurrentStep ?? setInternalActiveStep;

  const isLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  const isFirstStep: boolean = activeStep === 0;
  const isLastStep: boolean = activeStep === steps.length - 1;
  const currentStepData: WizardStep | undefined = steps[activeStep];
  const canNavigateToStep = (stepIndex: number): boolean => {
    if (nonLinear) return true;

    const maxCompletedStep = completedSteps.size > 0
      ? Math.max(...Array.from(completedSteps))
      : -1;

    return stepIndex <= Math.max(maxCompletedStep, activeStep);
  };



  const handleNext = async (): Promise<void> => {
    if (isLoading || !currentStepData) return;

    setLoading(true);
    resetStepError(activeStep);

    try {
      if (onStepChange) {
        await onStepChange(activeStep, 'next');
      }

      markStepAsCompleted(activeStep);

      if (isLastStep) {
        if (onComplete) {
          await onComplete();
        }
      } else {

        if (externalSetCurrentStep) {

          externalSetCurrentStep(activeStep + 1);
        } else {

          setInternalActiveStep(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Erro ao avançar step:', error);
      markStepAsError(activeStep);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async (): Promise<void> => {
    if (isLoading || isFirstStep) return;

    setLoading(true);
    resetStepError(activeStep);

    try {
      if (onStepChange) {
        await onStepChange(activeStep, 'back');
      }

      if (externalSetCurrentStep) {

        externalSetCurrentStep(activeStep - 1);
      } else {

        setInternalActiveStep(prev => prev - 1);
      }
    } catch (error) {
      console.error('Erro ao voltar step:', error);
      markStepAsError(activeStep);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    if (isLoading) return;
    if (onCancel) onCancel();
  };

  const handleStepClick = (stepIndex: number): void => {
    if (isLoading || stepIndex === activeStep || steps[stepIndex]?.disabled) return;

    if (!allowStepNavigation || !canNavigateToStep(stepIndex)) return;

    if (onStepClick) onStepClick(stepIndex);

    setActiveStep(stepIndex);
    resetStepError(stepIndex);
  };

  const isStepClickable = (stepIndex: number): boolean => {
    return allowStepNavigation &&
      canNavigateToStep(stepIndex) &&
      !steps[stepIndex]?.disabled &&
      !isLoading &&
      stepIndex !== activeStep;
  };

  const getStepStatus = (stepIndex: number) => {
    if (errorSteps.has(stepIndex)) return 'error';
    if (stepIndex === activeStep) return 'active';
    return 'default';
  };

  const isCurrentStepValid = currentStepData?.isValid !== false;

  return (
    <WizardContainer elevation={elevation} className={className} style={style}>

      <ProgressIndicator currentStep={activeStep} totalSteps={steps.length} showProgress={showProgress} />

      <Stepper 
        activeStep={activeStep} 
        orientation={orientation} 
        alternativeLabel={alternativeLabel} 
        nonLinear={nonLinear}
        sx={{
          '& .MuiStep-root': {
            padding: '0 24px',
          },
          '& .MuiStepConnector-root': {
            marginLeft: '24px',
            marginRight: '24px',
          }
        }}
      >
        {steps.map((step: WizardStep, index: number) => {
          const stepStatus = getStepStatus(index);
          const isError = stepStatus === 'error';
          const isActive = stepStatus === 'active';

          return (
            <Step key={step.id} disabled={step.disabled}>
              <StepLabel
                optional={step.optional ? <Typography variant="caption">Opcional</Typography> : undefined}
                error={isError}
                StepIconComponent={(props: StepIconProps) => (
                  <CustomStepIcon
                    {...props}
                    customIcon={step.icon}
                    inactiveIcon={step.inactiveIcon}
                    stepActive={isActive}
                    error={isError}
                  />
                )}
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: isStepClickable(index) ? 'pointer' : 'default',
                  '&:hover': { opacity: isStepClickable(index) ? 0.8 : 1 },
                  '& .MuiStepLabel-labelContainer': { 
                    color: isError ? 'error.main' : 'inherit',
                    marginTop: '16px',
                  },
                  '& .MuiStepLabel-iconContainer': {
                    paddingRight: '16px',
                  }
                }}
              >
                <Box sx={{ marginTop: '8px' }}>
                  <Typography variant={isActive ? "subtitle1" : "body2"} fontWeight={isActive ? "bold" : "normal"}>
                    {step.label}
                  </Typography>
                  {step.description && (
                    <Typography variant="caption" color="textSecondary" sx={{ marginTop: '4px', display: 'block' }}>
                      {step.description}
                    </Typography>
                  )}
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <ContentContainer>
        {currentStepData && (
          <Fade in={!isLoading} timeout={300}>
            <StepContent>
              {showContentHeader && (
                <>
                  <Typography variant="h5" gutterBottom fontWeight="medium" color="#83CF9C">
                    {currentStepData.label}
                  </Typography>
                  {currentStepData.description && (
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {currentStepData.description}
                    </Typography>
                  )}
                </>
              )}
              <Box sx={{ mt: 3 }}>{currentStepData.content}</Box>
            </StepContent>
          </Fade>
        )}
      </ContentContainer>

      <ButtonContainer>
        <Box>
          {showCancel && (
            <Button variant="outlined" color="error" onClick={handleCancel} disabled={isLoading} size="large">
              {cancelButtonText}
            </Button>
          )}
        </Box>
        <ButtonGroup>
          {(!isFirstStep || !hideBackButtonOnFirstStep) && (
            <Button
              variant="text"
              onClick={handleBack}
              disabled={isLoading}
              size="large"
              sx={{
                minWidth: 100,
                color: "#676A6C",
                '&.Mui-disabled': {
                  color: '#9E9E9E !important',
                  opacity: 1
                }
              }}
            >
              {backButtonText}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isLoading || !isCurrentStepValid}
            size="large"
            sx={{
              minWidth: 180,
              fontSize: '0.8rem',
              fontWeight: 'bold',
              boxShadow: 3,
              paddingX: 6,
              paddingY: 1,
              backgroundColor: '#83CF9C',
              color: 'white',
              '&:hover': { backgroundColor: '#66BB6A', boxShadow: 6 },
              '&.Mui-disabled': {
                backgroundColor: '#b2b2b2 !important',
                color: 'white !important',
                boxShadow: 'none !important',
                opacity: 1
              }
            }}
          >
            {isLoading ? loadingText : (isLastStep ? finishButtonText : nextButtonText)}
          </Button>
        </ButtonGroup>
      </ButtonContainer>
    </WizardContainer>
  );
};

export default Wizard;